import { useNavigate } from 'react-router-dom'
import { Icon } from './Icon'
import { useLivePulse } from '../hooks/useLivePulse'
import type { LiveTimelineItem, LiveFeedEntry } from '../types'

// LiveTab — green-gradient hero card on the regular event-tabs that
// launches the dedicated /live/:eventId surface (LiveDayView in PR L3).
//
// Visual contract: v5 prototype L1788-1827. Background gradient is
// linear-gradient(135deg, #0a4a3a 0%, #073629 100%) — the prototype's
// var(--green) → var(--green-dark) at literal hex (no `green-dark` in
// the Tailwind theme as of L2; that addition lands with the dark
// theme in L3 if we surface it more widely). Copy strings are exact
// per the work plan acceptance criteria; do not paraphrase.
//
// Below the hero: a 2-column grid showing Today's Schedule (preview)
// + Live Activity (feed). Both populate from useLivePulse — same
// endpoint that backs the full LiveDayView, so opening LiveTab and
// then navigating to /live/:eventId is a cache hit, not a refetch.

interface Props {
  eventId: number
}

export function LiveTab({ eventId }: Props) {
  const navigate = useNavigate()
  const { data, isLoading, error, secondsSinceUpdate } = useLivePulse(eventId)

  return (
    <div className="space-y-5">
      {/* Hero card — exact copy + gradient per prototype L1788-1799 */}
      <div
        className="rounded-xl p-7 flex items-center justify-between gap-6 flex-wrap"
        style={{
          background: 'linear-gradient(135deg, #0a4a3a 0%, #073629 100%)',
          color: '#fdfbf6'
        }}
      >
        <div className="flex-1 min-w-[260px]">
          <div
            className="text-[11px] uppercase font-semibold mb-1"
            style={{ color: 'rgba(253, 251, 246, 0.7)', letterSpacing: '0.1em' }}
          >
            Live Day View
          </div>
          <h2 className="font-display text-[22px] font-semibold leading-snug">
            Open the dedicated live view to manage today on the day.
          </h2>
          <p
            className="text-sm mt-1.5"
            style={{ color: 'rgba(253, 251, 246, 0.85)' }}
          >
            Mobile-friendly, role-aware. Volunteers see their duties,
            coordinators see the full picture.
          </p>
          {secondsSinceUpdate != null && (
            <div
              className="text-xs font-mono mt-3 inline-flex items-center gap-1.5"
              style={{ color: 'rgba(253, 251, 246, 0.6)' }}
            >
              <span
                className="inline-block w-1.5 h-1.5 rounded-full nymr-live-pulse-dot"
                style={{ background: '#80e8a8' }}
              />
              Updated {secondsSinceUpdate}s ago
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => navigate(`/live/${eventId}`)}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-md font-semibold text-base text-white border transition-colors hover:brightness-110"
          style={{ background: '#9b7f2a', borderColor: '#9b7f2a' }}
        >
          <Icon name="activity" size={17} />
          Launch Live View
        </button>
      </div>

      {/* Body grid — Today's Schedule preview + Live Activity feed */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Today's Schedule (preview) */}
        <section className="bg-white border border-line rounded-lg">
          <header className="px-5 py-3.5 border-b border-line flex items-center justify-between">
            <h3 className="font-display text-base font-semibold">Today's Schedule (preview)</h3>
            {data?.dayOfEvent != null && data.totalDays > 1 && (
              <span className="text-xs font-mono text-ink-soft">
                Day {data.dayOfEvent} of {data.totalDays}
              </span>
            )}
          </header>
          <div className="px-2 py-1">
            {isLoading && (
              <div className="px-3 py-4 text-sm text-ink-soft font-mono">Loading schedule…</div>
            )}
            {error && (
              <div className="m-3 bg-oxblood-soft border border-oxblood text-oxblood rounded p-3 text-sm">
                <div className="font-semibold mb-1">Could not load schedule</div>
                <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
              </div>
            )}
            {!isLoading && !error && (data?.timeline ?? []).length === 0 && (
              <div className="px-3 py-5 text-sm text-ink-soft">
                No services scheduled for {data?.targetDate ?? 'today'}.
              </div>
            )}
            {(data?.timeline ?? []).slice(0, 8).map((item, i, arr) => (
              <Step key={item.id} item={item} last={i === arr.length - 1} />
            ))}
          </div>
        </section>

        {/* Live Activity feed */}
        <section className="bg-white border border-line rounded-lg">
          <header className="px-5 py-3.5 border-b border-line">
            <h3 className="font-display text-base font-semibold">Live Activity</h3>
          </header>
          <div className="max-h-[400px] overflow-y-auto">
            {isLoading && (
              <div className="px-5 py-4 text-sm text-ink-soft font-mono">Loading activity…</div>
            )}
            {!isLoading && (data?.feed ?? []).length === 0 && (
              <div className="px-5 py-5 text-sm text-ink-soft">
                Nothing recorded in the last few hours.
              </div>
            )}
            {(data?.feed ?? []).map((entry, i, arr) => (
              <FeedRow key={entry.id} entry={entry} last={i === arr.length - 1} />
            ))}
          </div>
        </section>
      </div>
    </div>
  )
}

// ---- Step (Today's Schedule preview row) -----------------------------------
//
// Visual: 64px time column + flexible title/who column. Status drives
// colour + strikethrough. Mirrors the prototype's <Step> usage at
// L1804-1811 — same grid template, same colour mapping.

function Step({ item, last }: { item: LiveTimelineItem; last: boolean }) {
  const colour =
    item.status === 'done'
      ? '#5a5a5a'         // ink-soft
      : item.status === 'now'
        ? '#7a2329'       // oxblood
        : '#1a1a1a'       // ink

  const who = [item.origin, item.destination].filter(Boolean).join(' → ') || (item.loco ?? '')

  return (
    <div
      className="grid gap-3 px-3 py-2.5"
      style={{
        gridTemplateColumns: '64px 1fr auto',
        borderBottom: last ? 'none' : '1px solid #ece8df',
        alignItems: 'center'
      }}
    >
      <div className="font-mono font-semibold text-sm" style={{ color: colour }}>
        {item.time}
      </div>
      <div>
        <div
          className="text-sm font-semibold"
          style={{
            color: colour,
            textDecoration: item.status === 'done' ? 'line-through' : 'none'
          }}
        >
          {item.label}
        </div>
        {who && <div className="text-xs text-ink-soft mt-0.5">{who}</div>}
      </div>
      {item.status === 'now' && (
        <span
          className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold"
          style={{ background: '#ff8a65', color: '#1a1a1a', letterSpacing: '0.05em' }}
        >
          NOW
        </span>
      )}
      {item.status === 'next' && (
        <span
          className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold border border-line text-ink"
          style={{ letterSpacing: '0.05em' }}
        >
          NEXT
        </span>
      )}
      {item.status === 'done' && (
        <Icon name="check-circle" size={14} />
      )}
    </div>
  )
}

// ---- FeedRow (Live Activity entry) ----------------------------------------
//
// Visual: 52px time-mono column + message + actor name. Mirrors the
// prototype's feed rows at L1818-1822.

function FeedRow({ entry, last }: { entry: LiveFeedEntry; last: boolean }) {
  const time = formatClockTime(entry.occurredAt)
  return (
    <div
      className="grid gap-3 px-5 py-3"
      style={{
        gridTemplateColumns: '52px 1fr',
        borderBottom: last ? 'none' : '1px solid #ece8df'
      }}
    >
      <div className="font-mono text-xs text-ink-soft pt-0.5">{time}</div>
      <div>
        <div className="text-sm leading-snug">{entry.summary}</div>
        {entry.actorName && (
          <div className="text-xs text-ink-soft mt-0.5">{entry.actorName}</div>
        )}
      </div>
    </div>
  )
}

function formatClockTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '--:--'
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
}
